/**
 * `FAQAccordion` — single-open FAQ surface backed by `faqs.list({ slug })`.
 *
 * Behavioural notes:
 *   - Single-open: clicking a new question collapses the previously open
 *     panel. The accordion uses `@radix-ui/react-accordion` under the
 *     hood (this is what shadcn/ui's `Accordion` is built on; the
 *     project does not yet have a `ui/` directory so we consume Radix
 *     directly to avoid scaffolding an unused shadcn registry).
 *   - Smooth expand: Radix's `data-state` selectors animate a CSS
 *     `grid-template-rows: 0fr -> 1fr` transition rather than a
 *     `max-height` hack (per `.claude/rules/uncodixify-ui.md`).
 *   - a11y: Radix supplies `aria-expanded`, `aria-controls`, and pairs
 *     each panel with `role="region"` + `aria-labelledby` automatically.
 *
 * Locale handling: the API filters server-side by `ctx.locale`, so this
 * component just renders what it receives. Question numbering is purely
 * presentational ("01.", "02.").
 */

import * as Accordion from "@radix-ui/react-accordion";
import { useQuery } from "@tanstack/react-query";

import type { FaqItem } from "@hantavirus/shared";
import type { TranslateFn } from "#/lib/i18n.js";

import { Markdown } from "./Timeline.js";

export type FAQAccordionProps = {
  /** Outbreak-scoped FAQ; omit for global. */
  slug?: string;
  /** UI locale for the API call and display. */
  locale: string;
  /** Locale-resolved labels. */
  t: TranslateFn;
  /** Optional initial data from SSR. */
  initialItems?: FaqItem[];
  /** Optional API base override — used by tests. */
  apiBaseUrl?: string;
};

function buildApiUrl(base: string | undefined): string {
  const fallback =
    typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
      ? (import.meta.env.VITE_API_URL as string | undefined)
      : undefined;
  const resolved = (base ?? fallback ?? "").replace(/\/+$/u, "");
  return `${resolved}/api/trpc/faqs.list`;
}

async function fetchFaqs(
  slug: string | undefined,
  locale: string,
  apiBaseUrl?: string,
): Promise<FaqItem[]> {
  const inputObj: Record<string, unknown> = {};
  if (slug) inputObj.slug = slug;
  const encoded = encodeURIComponent(JSON.stringify({ json: inputObj }));
  const url = `${buildApiUrl(apiBaseUrl)}?input=${encoded}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { "x-locale": locale, accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) throw new Error(`faqs.list failed: ${res.status}`);
  const json = (await res.json()) as {
    result?: { data?: { json?: FaqItem[] } | FaqItem[] };
  };
  const inner = json.result?.data;
  const data =
    (Array.isArray(inner)
      ? inner
      : inner && typeof inner === "object" && "json" in inner
        ? (inner as { json: FaqItem[] }).json
        : undefined) ?? undefined;
  if (!data) throw new Error("faqs.list: malformed response");
  return data;
}

export function FAQAccordion({ slug, locale, t, initialItems, apiBaseUrl }: FAQAccordionProps) {
  const query = useQuery<FaqItem[], Error>({
    queryKey: ["faqs.list", { slug, locale }],
    queryFn: () => fetchFaqs(slug, locale, apiBaseUrl),
    initialData: initialItems,
    staleTime: 5 * 60 * 1000, // 5 min — FAQ content changes rarely.
  });

  const items = query.data ?? [];

  if (query.isLoading && items.length === 0) {
    return (
      <output
        aria-label={t("faq.loading")}
        style={{
          display: "block",
          paddingBlock: "2rem",
          textAlign: "center",
          color: "var(--color-fg-muted)",
          fontSize: "0.875rem",
        }}
      >
        {t("faq.loading")}
      </output>
    );
  }

  if (query.isError) {
    return (
      <p
        role="alert"
        style={{
          margin: 0,
          padding: "0.75rem 1rem",
          border: "1px solid var(--color-accent-emergency)",
          background: "hsl(354 78% 52% / 0.08)",
          color: "var(--color-accent-emergency)",
          borderRadius: "0.375rem",
          fontSize: "0.875rem",
        }}
      >
        {t("faq.error")}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p
        style={{
          margin: 0,
          paddingBlock: "1.5rem",
          textAlign: "center",
          color: "var(--color-fg-muted)",
        }}
      >
        {t("faq.empty")}
      </p>
    );
  }

  return (
    <section id="faq" aria-label={t("faq.ariaLabel")} style={{ display: "grid", gap: "0.75rem" }}>
      <h2
        style={{
          margin: 0,
          fontFamily: "var(--font-sans)",
          fontSize: "1.25rem",
          fontWeight: 600,
          color: "var(--color-fg-default)",
        }}
      >
        {t("faq.title")}
      </h2>

      <Accordion.Root type="single" collapsible className="faq-accordion">
        {items.map((item, i) => {
          const numbering = String(i + 1).padStart(2, "0");
          const value = `faq-${item.id}`;
          return (
            <Accordion.Item
              key={item.id}
              value={value}
              className="faq-item"
              style={{
                borderBlockEnd: "1px solid var(--color-border-subtle)",
              }}
            >
              <Accordion.Header asChild>
                <h3 style={{ margin: 0 }}>
                  <Accordion.Trigger
                    className="faq-trigger"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.875rem",
                      inlineSize: "100%",
                      paddingBlock: "1rem",
                      paddingInline: "0.25rem",
                      background: "transparent",
                      border: "none",
                      color: "var(--color-fg-default)",
                      fontFamily: "var(--font-sans)",
                      fontSize: "1rem",
                      fontWeight: 600,
                      textAlign: "start",
                      cursor: "pointer",
                      minHeight: "44px",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="faq-number"
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.8125rem",
                        color: "var(--color-fg-muted)",
                        flex: "0 0 auto",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {numbering}.
                    </span>
                    <span style={{ flex: 1 }}>{item.question}</span>
                    {/* Chevron — rotates on open via CSS data-state. */}
                    <span
                      aria-hidden="true"
                      className="faq-chevron"
                      style={{
                        flex: "0 0 auto",
                        color: "var(--color-fg-muted)",
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.875rem",
                        transition: "transform 150ms ease",
                      }}
                    >
                      ▾
                    </span>
                  </Accordion.Trigger>
                </h3>
              </Accordion.Header>
              <Accordion.Content className="faq-content" forceMount>
                <div
                  style={{
                    paddingBlockEnd: "1rem",
                    paddingInline: "0.25rem",
                    color: "var(--color-fg-muted)",
                    fontSize: "0.9375rem",
                    lineHeight: 1.6,
                  }}
                >
                  <Markdown source={item.answer} />
                </div>
              </Accordion.Content>
            </Accordion.Item>
          );
        })}
      </Accordion.Root>
    </section>
  );
}
